import React, { useState } from 'react';
import { InputNumber, Button, Form, Table, Popconfirm, Space, Tag } from 'antd';
import {
  MinusCircleOutlined,
  PlusCircleOutlined,
} from '@ant-design/icons';

import MoneyTitle from '../customer/MoneyTitle';
import StrikeTeamModel from '../../models/StrikeTeamModel';

const limits = {
  min: 1,
  max: 100,
  countMin: 2,
  countMax: 5,
};

const TeamEditorPercents = ({ teamId, team, defaultTag, onLoading }) => {
  const { updatedPercentsAt, selectedValueByIndex } = team || {};
  const countPercents = team.percents ? team.percents.length : 0;
  const midIndex = Math.floor(countPercents / 2);
  const useSelectedValueByIndex = selectedValueByIndex === undefined ? midIndex : selectedValueByIndex;
  const defaultSelectedValueByIndex = useSelectedValueByIndex > countPercents ? countPercents : useSelectedValueByIndex;

  const [form] = Form.useForm();
  const [percents, setPercents] = useState(team.percents || []);
  const [newValue, setNewValue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [isChange, setIsChange] = useState(false);
  const [defaultOption, setDefaultOption] = useState(defaultSelectedValueByIndex); // Default option state

  const model = new StrikeTeamModel({ teamId });
  const tagColor = '#666';

  const displayPercents = percents;

  const handleSave = async ({ percents, selectedValueByIndex }) => {
    setLoading(true);
    onLoading && onLoading({ message: `Saving percentages ...` });
    const sortedPercents = percents.sort((a, b) => a - b);
    await model.saveTeamPercents({ percents: sortedPercents, selectedValueByIndex });
    onLoading && onLoading({ message: null });
    setLoading(false);
    setIsChange(false);
  };

  const handleClear = () => {
    setPercents([]);
    setIsChange(true);
  }

  const handleAddPercent = (percent) => {
    setPercents([...percents, percent * 100]); // Store as hundredths of a percent
    setIsChange(true);
  };

  const handleDeletePercent = (percent) => {
    setPercents(percents.filter((p) => p !== percent));
    setIsChange(true);
  };

  const handleUpdatePercent = (position, nextValue) => {
    if (nextValue === null || nextValue === undefined) return;
    const nextAmount = Math.round(parseFloat(nextValue) * 100);
    if (Number.isNaN(nextAmount)) return;
    const isInvalid = nextValue < limits.min || nextValue > limits.max;
    if (isInvalid) return;
    const updatedPercents = [...percents];
    if (updatedPercents[position] === nextAmount) return;
    if (updatedPercents.includes(nextAmount)) return;
    updatedPercents[position] = nextAmount;
    setPercents(updatedPercents);
    setIsChange(true);
  };

  const handleFinish = (formData) => {
    const { amount } = formData;
    if (amount) {
      handleAddPercent(parseFloat(amount));
    }
    form.resetFields();
  };

  const handleSelectedDefaultRow = (index) => {
    setDefaultOption(index);
    setIsChange(true);
  }

  const isAddDisabled = () => {
    const newValueCent = parseFloat(newValue) * 100;
    const isInValues = percents.includes(newValueCent);
    return percents.length >= limits.countMax || newValue < limits.min || newValue > limits.max || isInValues;
  }

  const onChangeNewValue = (v) => {
    setNewValue(v);
  }

  const isSaveDisabled = () => {
    if (!isChange) {
      return true;
    }
    if (percents.length === 0) {
      return false;
    }
    return percents.length < limits.countMin || percents.length > limits.countMax;
  }

  const columns = [
    {
      title: `Percentages (min: ${limits.countMin}, max: ${limits.countMax})`,
      dataIndex: 'amount',
      key: 'amount',
      render: (text, record) =>
        record.type === 'percent' ?
          <div className='d-flex align-items-center justify-content-between'>
            <div style={{ flexGrow: 1 }}></div>

            <InputNumber
              min={limits.min}
              max={limits.max}
              step={0.01}
              value={record.amount / 100}
              onChange={(value) => handleUpdatePercent(record.position, value)}
              onFocus={(event) => event.target.select()}
              style={{ minWidth: '120px', textAlign: 'center', fontWeight: 'bold', fontSize: '16px' }}
              formatter={(value) => (value === undefined || value === null ? '' : `${value}%`)}
              parser={(value) => value.replace(/[%,\s]/g, '')}
              controls={false}
            />
            <div className='mx-2 my-0'>
              <Popconfirm
                title={`Are you sure you want to delete?`}
                onConfirm={() =>
                  handleDeletePercent(record.amount)
                }
              >
                <Button
                  type="danger" icon={<MinusCircleOutlined />}
                  style={{ maxHeight: '28px', minWidth: '40px' }}
                />
              </Popconfirm>
            </div>
            {/* Spacer Div */}
            <div style={{ flexGrow: 1 }}></div>
            <Button
              type={record.position === defaultOption ? "primary" : "ghost"}
              onClick={() => handleSelectedDefaultRow(record.position)}
              style={{ minWidth: '120px', fontWeight: 'bold', fontSize: '12px' }}
            >
              {record.position === defaultOption ? "Default" : "Make default"}
            </Button>

            <div style={{ flexGrow: 1 }}></div>
          </div>
          :
          <div className='d-flex align-items-center justify-content-between'>
            <div style={{ flexGrow: 1 }}></div>

            <Form layout="inline" form={form} onFinish={handleFinish}>
              <Form.Item name="amount">
                <InputNumber
                  min={limits.min}
                  max={limits.max}
                  step={0.01}
                  value={newValue}
                  onChange={onChangeNewValue}
                  onFocus={(event) => event.target.select()}
                  placeholder="Add value"
                  style={{ minWidth: '120px' }}
                  formatter={(value) => (value === undefined || value === null ? '' : `${value}%`)}
                  parser={(value) => value.replace(/[%,\s]/g, '')}
                  controls={false}
                />
              </Form.Item>
              <div style={{ width: '40px'}}></div>
              <Form.Item>
                <Button
                  type="primary"
                  htmlType="submit"
                  disabled={isAddDisabled()}
                  icon={<PlusCircleOutlined />}
                  style={{ minWidth: '80px' }}
                >Add</Button>
              </Form.Item>
            </Form>

            <div style={{ flexGrow: 1 }}></div>
          </div>
      ,
    },
  ];

  const data = [
    ...displayPercents.map((p, i) => ({ key: `percent-${i}`, type: 'percent', amount: p, position: i })),
    { key: 'value-add-new', type: 'add', amount: 0, position: displayPercents.length },
  ];

  return (
    <div className="container mt-4">
      <h3>Set up Percentages</h3>

      {percents.length > 0 && (
        <div className="mt-4">
          Percentages override the pre-set values. Please clear all percentages to use values.
        </div>
      )}

      <Table
        className="mt-4"
        columns={columns}
        dataSource={data}
        pagination={false}
        bordered
      />

      <Popconfirm
        title="Percentages override any pre-set values. Are you sure?"
        okText="Yes, save"
        cancelText="Cancel"
        onConfirm={() => handleSave({ percents, selectedValueByIndex: defaultOption })}
        disabled={isSaveDisabled()}
      >
        <Button
          type="primary"
          style={{ marginTop: '12px', marginLeft: '-24px' }}
          loading={loading}
          disabled={isSaveDisabled()}
        >
          {'Save Changes'}
        </Button>
      </Popconfirm>

      <Button
        type="danger"
        onClick={() => handleClear()}
        style={{ marginTop: '12px', marginLeft: '16px' }}
        loading={loading}
        disabled={percents.length === 0}
        icon={<MinusCircleOutlined />}
      >
        {'Clear All'}
      </Button>

      { updatedPercentsAt && (
        <div className="mt-0">
          <small>Last updated: {updatedPercentsAt.toDate().toLocaleString()}</small>
        </div>
      )}

    </div>
  );
};

export default TeamEditorPercents;
